import { Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { UserService } from '../user/user.service';
import {
    BehaviorSubject,
    combineLatest,
    filter,
    map,
    Observable,
    startWith,
    Subject,
} from 'rxjs';
import { McpActionParam, McpRegisteredAction, McpActionRegistryService } from './mcp-action-registry.service';
import { McpPageAction, PageActionService } from './page-action.service';
import { McpTraceService } from './mcp-trace.service';

export type McpAwarenessMode = 'screen' | 'away';

export interface McpActionLogEntry {
    at: string;
    type: string;
    detail: string;
    result?: string;
}

export interface McpMergedAction {
    action: string;
    description: string;
    source: 'manifest' | 'directive';
    params?: McpActionParam[];
}

export interface McpContextSnapshot {
    route: string;
    pageLabel: string;
    role: string;
    userName: string;
    /**
     * True for shared, public stations (the kiosk) where the assistant must
     * NOT know or use the signed-in account's name. Drives an anonymized
     * identity locally and a "no-name" override in the backend prompt.
     */
    anonymous: boolean;
    awareness: McpAwarenessMode;
    focus: any;
    dataSummary: string;
    actions: McpMergedAction[];
    actionLog: McpActionLogEntry[];
}

@Injectable({ providedIn: 'root' })
export class McpContextService {
    private readonly _awareness = new BehaviorSubject<McpAwarenessMode>('screen');
    private readonly _actionLog = new BehaviorSubject<McpActionLogEntry[]>([]);
    private readonly _contextUpdate = new Subject<string>();
    private readonly _visitEnded = new Subject<void>();
    private readonly _visitStarted = new Subject<void>();
    private _lastSnapshot: McpContextSnapshot | null = null;
    private _updateTimer: any = null;

    readonly awareness$ = this._awareness.asObservable();
    readonly contextUpdate$ = this._contextUpdate.asObservable();
    /** Fires when a kiosk voice visit ends (end_conversation). */
    readonly visitEnded$ = this._visitEnded.asObservable();
    /** Fires when a kiosk voice visit begins (wake word "Yo"). */
    readonly visitStarted$ = this._visitStarted.asObservable();

    readonly snapshot$: Observable<McpContextSnapshot>;

    constructor(
        private readonly router: Router,
        private readonly userService: UserService,
        private readonly pageActionService: PageActionService,
        private readonly actionRegistry: McpActionRegistryService,
        private readonly traceService: McpTraceService
    ) {
        const route$ = this.router.events.pipe(
            filter((e): e is NavigationEnd => e instanceof NavigationEnd),
            map(() => this.router.url),
            startWith(this.router.url)
        );

        this.snapshot$ = combineLatest([
            route$,
            this.userService.user$.pipe(startWith(null)),
            this.pageActionService.manifest$.pipe(startWith(null)),
            this.actionRegistry.actions$.pipe(startWith([] as McpRegisteredAction[])),
            this.pageActionService.focus$.pipe(startWith(null)),
            this.awareness$,
            this._actionLog,
        ]).pipe(
            map(([route, user, manifest, registryActions, focus, awareness, log]) => {
                const merged = this.mergeActions(manifest?.actions ?? [], registryActions);
                const anonymous = this.isAnonymousStation(route);
                // On a shared kiosk station the signed-in account is not the
                // person standing there, so never expose its name.
                const userName = anonymous
                    ? 'Guest'
                    : this.resolveUserName(user as any);
                const userRole = this.resolveUserRole(user as any);

                const snapshot: McpContextSnapshot = {
                    route,
                    pageLabel: manifest?.pageLabel ?? 'Unknown Page',
                    role: userRole,
                    userName,
                    anonymous,
                    awareness,
                    focus,
                    dataSummary: manifest?.getDataSummary?.() ?? '',
                    actions: merged,
                    actionLog: log,
                };

                this._lastSnapshot = snapshot;
                return snapshot;
            })
        );

        this.snapshot$.subscribe();
    }

    getSnapshot(): McpContextSnapshot {
        if (!this._lastSnapshot) {
            const anonymous = this.isAnonymousStation(this.router.url);
            return {
                route: this.router.url,
                pageLabel: 'Unknown Page',
                role: 'Unknown',
                userName: anonymous ? 'Guest' : 'Unknown User',
                anonymous,
                awareness: 'screen',
                focus: null,
                dataSummary: '',
                actions: [],
                actionLog: [],
            };
        }
        const manifest = this.pageActionService.getActiveManifest?.();
        return {
            ...this._lastSnapshot,
            dataSummary: manifest?.getDataSummary?.() ?? this._lastSnapshot.dataSummary,
        };
    }

    setAwareness(mode: McpAwarenessMode): void {
        this._awareness.next(mode);
        this.logAction('awareness_change', `Awareness set to ${mode}`);
        this.pushRealtimeContextUpdate('awareness_change');
    }

    logAction(type: string, detail: string, result?: string): void {
        const next: McpActionLogEntry[] = [
            ...this._actionLog.value,
            { at: new Date().toISOString(), type, detail, result },
        ].slice(-40);
        this._actionLog.next(next);
        this.traceService.log(
            'context_action_log',
            { type, detail, result },
            { detail: 'Context action logged' }
        );
    }

    clearActionLog(): void {
        this._actionLog.next([]);
    }

    /** Signal that a voice visit ended — kiosk pages reset UI state. */
    notifyVisitEnded(): void {
        this.clearActionLog();
        this._visitEnded.next();
    }

    /** Signal that a voice visit began — kiosk pages clear filters/search. */
    notifyVisitStarted(): void {
        this.clearActionLog();
        this._visitStarted.next();
    }

    getAvailableActions(): McpMergedAction[] {
        return this._lastSnapshot?.actions ?? [];
    }

    private mergeActions(
        manifestActions: McpPageAction[],
        registryActions: McpRegisteredAction[]
    ): McpMergedAction[] {
        const fromManifest: McpMergedAction[] = manifestActions
            .filter((a) => !a.available || a.available())
            .map((a) => ({
                action: a.action,
                description: a.description,
                source: 'manifest' as const,
                params: a.params,
            }));

        const manualNames = new Set(fromManifest.map((a) => a.action));

        const fromRegistry: McpMergedAction[] = registryActions
            .filter((a) => !manualNames.has(a.action))
            .map((a) => ({
                action: a.action,
                description: a.description,
                source: 'directive' as const,
                params: a.params,
            }));

        return [...fromManifest, ...fromRegistry];
    }

    /**
     * Shared/public stations (the kiosk) run under a generic account, so the
     * assistant must stay anonymous and never address the operator by name.
     */
    private isAnonymousStation(route: string): boolean {
        return String(route ?? '')
            .toLowerCase()
            .split(/[?#]/, 1)[0]
            .includes('/kiosk');
    }

    private resolveUserName(user: any): string {
        if (!user || typeof user !== 'object') {
            return 'Unknown User';
        }

        const directName = String(user.name ?? user.full_name ?? user.user_name ?? '').trim();
        if (directName) {
            return directName;
        }

        const first = String(user.first_name ?? user.firstname ?? '').trim();
        const last = String(user.last_name ?? user.lastname ?? '').trim();
        const composed = `${first} ${last}`.trim();
        if (composed) {
            return composed;
        }

        return 'Unknown User';
    }

    private resolveUserRole(user: any): string {
        if (!user || typeof user !== 'object') {
            return 'Unknown';
        }
        const role = String(user.role ?? user.user_role ?? '').trim();
        return role || 'Unknown';
    }

    buildPromptContext(snapshot: McpContextSnapshot = this.getSnapshot()): string {
        const priorityActions = new Set([
            'next_page',
            'previous_page',
            'search',
            'set_filter',
            'open_receiving_order',
            'open_shipping_order',
            'view_order',
            'close_drawer',
            'refresh',
            'filter_not_received',
            'filter_not_shipped',
            'filter_completed_not_shipped',
        ]);
        const prioritized = [
            ...snapshot.actions.filter((a) => priorityActions.has(a.action)),
            ...snapshot.actions.filter((a) => !priorityActions.has(a.action)),
        ];
        const actionLines = prioritized
            .slice(0, 120)
            .map((a) => {
                let line = `- "${a.action}" — ${a.description}`;
                if (a.params?.length) {
                    const paramParts = a.params.map((p) => {
                        if (p.enum?.length) {
                            return `${p.name}=${p.enum.join('|')}`;
                        }
                        return p.name;
                    });
                    line += ` Params: { ${paramParts.join(', ')} }`;
                }
                return line;
            })
            .join('\n');
        const logLines = snapshot.actionLog
            .slice(-10)
            .map((l) => `- [${l.at}] ${l.type}: ${l.detail}${l.result ? ` -> ${l.result}` : ''}`)
            .join('\n');

        return [
            `PAGE: ${snapshot.pageLabel} (${snapshot.route})`,
            snapshot.anonymous
                ? `USER: unknown (${snapshot.role}) — shared kiosk station; the operator's name is NOT known. Do not use or ask for a name; address them generically.`
                : `USER: ${snapshot.userName} (${snapshot.role})`,
            `AWARENESS: ${snapshot.awareness}`,
            snapshot.focus ? `FOCUS: ${JSON.stringify(snapshot.focus)}` : 'FOCUS: none',
            snapshot.dataSummary ? `DATA SUMMARY: ${snapshot.dataSummary}` : 'DATA SUMMARY: none',
            'AVAILABLE ACTIONS:',
            actionLines || '- none',
            'SESSION LOG:',
            logLines || '- none',
        ].join('\n');
    }

    pushRealtimeContextUpdate(trigger = 'manual'): void {
        if (this._updateTimer) {
            clearTimeout(this._updateTimer);
        }

        this._updateTimer = setTimeout(() => {
            const text = this.buildPromptContext();
            this._contextUpdate.next(`[context_update:${trigger}]\n${text}`);
        }, 200);
    }
}
